import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ApplicationProcess = () => {
  const [activeStep, setActiveStep] = useState(1);

  const steps = [
    {
      id: 1,
      title: "Submit Application",
      description: "Complete our comprehensive application form with your business details, certifications, and experience.",
      duration: "5 minutes",
      requirements: [
        "Valid contractor license",
        "Proof of insurance ($1M+ liability)",
        "3+ years roofing experience",
        "Business registration documents"
      ]
    },
    {
      id: 2,
      title: "Background Verification",
      description: "We conduct thorough background checks, verify credentials, and review your work history.",
      duration: "2-3 business days",
      requirements: [
        "Criminal background check",
        "License verification",
        "Insurance validation",
        "Reference checks"
      ]
    },
    {
      id: 3,
      title: "Skills Assessment",
      description: "Complete our roofing knowledge test and participate in a technical interview with our team.",
      duration: "1 hour",
      requirements: [
        "Technical knowledge test",
        "Safety protocol assessment",
        "Customer service evaluation",
        "Quality standards review"
      ]
    },
    {
      id: 4,
      title: "Onboarding & Training",
      description: "Learn our platform, processes, and quality standards through comprehensive training modules.",
      duration: "1-2 days",
      requirements: [
        "Platform training completion",
        "Quality standards certification",
        "Customer communication protocols",
        "Digital tools mastery"
      ]
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center space-y-6 mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full">
            <Icon name="ClipboardCheck" size={16} className="text-primary mr-2" />
            <span className="text-sm font-medium text-primary">Application Process</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-jakarta font-bold text-text-primary">
            Simple 4-Step Application Process
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            We maintain high standards to ensure quality for our customers and success for our partners. 
            Here's what to expect during the application process.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            {steps?.map((step, index) => (
              <div key={step?.id} className="flex items-center">
                <button
                  onClick={() => setActiveStep(step?.id)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-jakarta font-bold text-sm transition-all duration-300 ${
                    activeStep === step?.id
                      ? 'bg-primary text-white shadow-lg scale-110'
                      : activeStep > step?.id
                      ? 'bg-success text-white' :'bg-white border-2 border-border text-text-secondary hover:border-primary'
                  }`}
                >
                  {activeStep > step?.id ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    step?.id
                  )}
                </button>
                
                {index < steps?.length - 1 && (
                  <div className={`w-16 lg:w-24 h-1 mx-4 transition-colors duration-300 ${
                    activeStep > step?.id ? 'bg-success' : 'bg-border'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Active Step Content */}
        <div className="bg-white rounded-2xl shadow-trust p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-jakarta font-bold text-white">{activeStep}</span>
                </div>
                <div>
                  <h3 className="text-2xl font-jakarta font-bold text-text-primary">
                    {steps?.[activeStep - 1]?.title}
                  </h3>
                  <div className="flex items-center space-x-2 mt-2">
                    <Icon name="Clock" size={16} className="text-text-secondary" />
                    <span className="text-sm text-text-secondary">
                      {steps?.[activeStep - 1]?.duration}
                    </span>
                  </div>
                </div>
              </div>
              
              <p className="text-lg text-text-secondary leading-relaxed">
                {steps?.[activeStep - 1]?.description}
              </p>
              
              <div className="space-y-4">
                <h4 className="font-jakarta font-semibold text-text-primary">Requirements:</h4>
                <ul className="space-y-3">
                  {steps?.[activeStep - 1]?.requirements?.map((requirement, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
                      <span className="text-text-secondary">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              {/* Timeline Visual */}
              <div className="bg-surface rounded-xl p-6">
                <h4 className="font-jakarta font-semibold text-text-primary mb-4">Application Timeline</h4>
                <div className="space-y-4">
                  {steps?.map((step) => (
                    <div 
                      key={step?.id}
                      className={`flex items-center space-x-4 p-3 rounded-lg transition-colors duration-300 ${
                        step?.id === activeStep ? 'bg-primary/10' : 'bg-white'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        step?.id === activeStep
                          ? 'bg-primary text-white'
                          : step?.id < activeStep
                          ? 'bg-success text-white' :'bg-border text-text-secondary'
                      }`}>
                        {step?.id < activeStep ? (
                          <Icon name="Check" size={12} />
                        ) : (
                          step?.id
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-text-primary">{step?.title}</div>
                        <div className="text-xs text-text-secondary">{step?.duration}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-6 text-center">
                <h4 className="font-jakarta font-semibold text-text-primary mb-2">Ready to Get Started?</h4>
                <p className="text-sm text-text-secondary mb-4">
                  Join 500+ contractors already growing their business with OpenbooQ
                </p>
                <Button variant="default" size="lg" fullWidth>
                  Start Application
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center space-x-4 mt-8">
          <Button
            variant="outline"
            onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
            disabled={activeStep === 1}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Previous
          </Button>
          <Button
            variant="default"
            onClick={() => setActiveStep(Math.min(4, activeStep + 1))}
            disabled={activeStep === 4}
            iconName="ChevronRight"
            iconPosition="right"
          >
            Next Step
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ApplicationProcess;